import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  Spinner,
  Pills,
  Pill,
  IconPill,
  DumbSelectionGroup,
  Checkbox,
  Radio,
  Link,
  Icon,
  UtilsBlock,
  Tabs,
  Tab,
  TabPanel,
  Dropdown,
} from '@hz-design-system/web-ui';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import EnvironmentContext from '../../../contexts/EnvironmentContext';
import SearchFeedItem from './SearchFeedItem';
import getToysItems from '../repositories/getToysItems';
import isPageReloaded from '../../CarsSearch/utils/isPageReloaded';

import Classes from './SearchFeed.scss';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

type TSearchFeedProps = {
  searchResult: any;
  searchCategories?: any;
  searchFilters?: any;
  searchLink?: string;
};

type TOffset = {
  top?: string;
  right?: string;
  left?: string;
  marginRight?: string;
};

const BODY_SCROLL_BLOCK_CLASS = 'ReactModal__Body--open';
const PRICE_FILTER_OPTIONS = [
  {
    id: 'price-1',
    value: 5,
    labelText: 'Tot €5,-',
  },
  {
    id: 'price-2',
    value: 10,
    labelText: 'Tot €10,-',
  },
  {
    id: 'price-3',
    value: 20,
    labelText: 'Tot €20,-',
  },
  {
    id: 'price-4',
    value: 50,
    labelText: 'Tot €50,-',
  },
];

const MILEAGE_OPTIONS: any = [
  {
    label: 'Alle',
    value: 0,
  },
  {
    label: '2.500 km',
    value: 2501,
  },
  {
    label: '5.000 km',
    value: 5001,
  },
  {
    label: '10.000 km',
    value: 10001,
  },
  {
    label: '20.000 km',
    value: 20001,
  },
  {
    label: '30.000 km',
    value: 30001,
  },
  {
    label: '40.000 km',
    value: 40001,
  },
  {
    label: '50.000 km',
    value: 50001,
  },
  {
    label: '60.000 km',
    value: 60001,
  },
  {
    label: '70.000 km',
    value: 70001,
  },
  {
    label: '80.000 km',
    value: 80001,
  },
  {
    label: '80.000 km',
    value: 90001,
  },
  {
    label: '100.000 km',
    value: 100001,
  },
  {
    label: '125.000 km',
    value: 125001,
  },
  {
    label: '150.000 km',
    value: 150001,
  },
  {
    label: '175.000 km',
    value: 175001,
  },
  {
    label: '200.000 km',
    value: 200001,
  },
  {
    label: '250.000 km',
    value: 250001,
  },
  {
    label: '300.000 km',
    value: 300001,
  },
  {
    label: '400.000 km',
    value: 400001,
  },
  {
    label: '500.000 km',
    value: 500001,
  },
  {
    label: '600.000 km',
    value: 600001,
  },
  {
    label: '700.000 km',
    value: 700001,
  },
  {
    label: '800.000 km',
    value: 800001,
  },
  {
    label: '900.000 km',
    value: 900001,
  },
  {
    label: '1.000.000 km',
    value: 1000001,
  },
];

const FILTERS = {
  toys: ['l1', 'l2', 'price'],
  'car-upgrade': ['l2Category', 'mileage', 'fuel'],
  'upgrade-auto': ['fuel', 'transmission', 'body', 'options'],
  overdrive: ['options'],
};

const MAX_L2_FILTERS = 3;
const HIGHLIGHTED_OPTION_IDS = [13975, 11787, 13974, 13963, 13964, 11804, 11550, 11795, 11791, 11561, 11564];

const SearchFeed = ({ searchResult, searchCategories, searchFilters, searchLink }: TSearchFeedProps) => {
  const { t } = useI18nContext();
  const { isInApp, pageKey } = useContext(EnvironmentContext);
  const didMountRef = useRef(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const filterValuesRef = useRef<HTMLDivElement>(null);
  const [modelFilter, setModelFilter] = useState({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [listings, setListings] = useState<any>(searchResult);
  const [l1Filters, setL1Filters] = useState<any>([]);
  const [appliedFilters, setAppliedFilters] = useState<any>([]);
  const [l2Filters, setL2Filters] = useState<any>([]);
  const [priceFilter, setPriceFilter] = useState<any>(null);
  const [mileageFilter, setMileageFilter] = useState<any>([null, null]);
  const [filterOptions, setFilterOptions] = useState<any>([]);
  const [currentFilterId, setCurrentFilterId] = useState(null);
  const [filterPanelOffset, setFilterPanelOffset] = useState<{}>();
  const [filterChanged, setFilterChanged] = useState(false);
  const [showMoreFilterOptions, setShowMoreFilterOptions] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const fetchItems = useCallback(
    async ({
      key,
      page,
      l1Filters: l1s,
      l2Filters: l2s,
      priceFilter: price,
      mileageFilter: mileage,
      filters: f,
      p,
    }) => {
      setIsLoading(true);
      const { listings: newListings, facets } = await getToysItems(key, page, l1s, l2s, price, f, p, mileage);
      setHasNextPage(Boolean(newListings?.length));
      setIsLoading(false);
      return { newListings, facets };
    },
    [],
  );

  useEffect(() => {
    if (didMountRef.current) {
      window.history.replaceState({ listings, l1Filters, l2Filters }, '', window.location.href);
    } else {
      didMountRef.current = true;
    }
  }, [l1Filters, l2Filters, listings]);

  useEffect(() => {
    const stateFromHistory = window?.history?.state;
    if (stateFromHistory && !isPageReloaded()) {
      setListings(stateFromHistory.listings);
      setL1Filters(stateFromHistory.l1Filters);
      setL2Filters(stateFromHistory.l2Filters);
    }
  }, []);

  const onLoadMore = async () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    const { newListings } = await fetchItems({
      key: pageKey,
      page: nextPage,
      l1Filters,
      l2Filters,
      priceFilter,
      filters: appliedFilters,
      p: false,
      mileageFilter,
    });
    if (newListings.length) {
      const duplicatedListings = newListings.filter(({ itemId }) => listings.find(({ itemId: id }) => id === itemId));
      const deduplicatedListings = newListings.filter(
        ({ itemId }) => !listings.find(({ itemId: id }) => id === itemId),
      );
      if (duplicatedListings.length) {
        // eslint-disable-next-line no-console
        console.log(duplicatedListings);
      }
      setListings((prevListings) => [...prevListings, ...deduplicatedListings]);
    }
  };

  const infiniteRef = useInfiniteScroll<HTMLDivElement>({
    loading: isLoading,
    hasNextPage: hasNextPage,
    onLoadMore,
  });

  const onFilter = async (option, type) => {
    let newL1Filters = l1Filters;
    let newL2Filters = l2Filters;
    let newPriceFilter = priceFilter;
    const newMileageFilter = mileageFilter;
    let newFilters = appliedFilters;

    if (option.groupId) {
      if (l2Filters.find(({ value }) => value === option.value)) {
        newL2Filters = l2Filters.filter(({ value }) => value !== option.value);
      } else {
        newL2Filters = [...l2Filters, option];
      }
      setL2Filters(newL2Filters);
    } else if (type === 'price') {
      newPriceFilter = option.value;
      setPriceFilter(newPriceFilter);
    } else if (type === 'mileageFrom') {
      newMileageFilter[0] = option;
      setMileageFilter(newMileageFilter);
    } else if (type === 'mileageTo') {
      newMileageFilter[1] = option;
      setMileageFilter(newMileageFilter);
    } else if (type === 'category') {
      if (l1Filters.find(({ value }) => value === option.value)) {
        newL1Filters = l1Filters.filter(({ value }) => value !== option.value);
      } else {
        newL1Filters = [...l1Filters, option];
      }
      setL1Filters(newL1Filters);
    } else {
      if (appliedFilters.find((id) => id === option.id)) {
        newFilters = appliedFilters.filter((id) => id !== option.id);
      } else {
        newFilters = [...appliedFilters, option.id];
      }
      setAppliedFilters(newFilters);
    }

    const { newListings, facets } = await fetchItems({
      key: pageKey,
      page: 0,
      l1Filters: newL1Filters,
      l2Filters: newL2Filters,
      priceFilter: newPriceFilter,
      mileageFilter: newMileageFilter,
      filters: newFilters,
      p: type === 'l2Category',
    });

    const hasModelFacet = facets?.find(({ key }) => key === 'model');
    if (hasModelFacet && option.id.toString().includes('cat-')) {
      const l2CategoryFilter = searchFilters.find(({ key }) => key === 'l2Category');
      const selectedBrand = l2CategoryFilter.attributeGroup.find(
        ({ attributeValueId }) => attributeValueId === option.id,
      );
      const selectedBrandName = selectedBrand.attributeValueLabel;
      const newModelFilter = modelFilter;

      if (newModelFilter[selectedBrandName]) {
        delete newModelFilter[selectedBrandName];
      } else {
        // only new models
        const modelFacet = facets?.find(({ key, id }) => {
          if (key !== 'model') {
            return;
          }
          const modelFacetIds = Object.keys(modelFilter).map((brandName) => modelFilter[brandName].id);
          return !modelFacetIds.includes(id);
        });
        if (modelFacet) {
          newModelFilter[selectedBrandName] = modelFacet;
          newModelFilter[selectedBrandName].options = newModelFilter[selectedBrandName].attributeGroup.map(
            ({ attributeValueId, attributeValueLabel, attributeValueKey }) => ({
              id: attributeValueId,
              value: attributeValueKey,
              labelText: attributeValueLabel,
              hidden: false,
            }),
          );
        }
      }
      setModelFilter(newModelFilter);
    }

    setCurrentPage(0);
    setListings(newListings);
    setFilterChanged(true);
  };

  const toggleFilter = (event, type, categoryId, attributeGroup?: []) => {
    event.currentTarget.blur();
    if (filterChanged && filterValuesRef.current) {
      filterValuesRef.current.scrollTop = 0;
    }

    if (categoryId === null || currentFilterId === categoryId) {
      setCurrentFilterId(null);
      document.body.classList.remove(BODY_SCROLL_BLOCK_CLASS);
      if (filterChanged && filterValuesRef.current && filterRef.current && 'scrollIntoView' in filterRef.current) {
        filterRef.current.scrollIntoView({ behavior: 'smooth' });
        filterValuesRef.current.scrollTop = 0;
        setFilterChanged(false);
      }
    } else {
      setCurrentFilterId(categoryId);
      let options;
      if (categoryId === 0) {
        options = searchCategories.l1Categories;
      } else if (categoryId === 'price') {
        options = PRICE_FILTER_OPTIONS;
      } else if (categoryId === 'model') {
        options = [];
      } else if (type === 'l1') {
        options = searchCategories.l2Categories[categoryId];
      } else if (attributeGroup) {
        options = attributeGroup.map(({ attributeValueId, attributeValueLabel, attributeValueKey }) => ({
          id: attributeValueId,
          value: attributeValueKey,
          labelText: attributeValueLabel,
          hidden: type === 'options' && !HIGHLIGHTED_OPTION_IDS.includes(attributeValueId),
        }));
      }

      setFilterOptions(options);
      const rect = event.currentTarget.getBoundingClientRect();
      const offset: TOffset = {};

      if (rect.left + 640 > window.innerWidth) {
        offset.top = `${rect.top}px`;
        offset.right = `${rect.right - event.currentTarget.parentNode.clientWidth}px`;
        offset.left = 'unset';
        offset.marginRight = 'auto';
      } else {
        offset.top = `${rect.top}px`;
        offset.left = `${rect.left}px`;
      }
      document.body.classList.add(BODY_SCROLL_BLOCK_CLASS);
      setFilterPanelOffset(offset);
    }
  };

  const toggleFavorite = (id) => {
    const listingIndex = listings.findIndex(({ itemId }) => itemId === id);

    if (listingIndex >= 0) {
      const newListings = [...listings];
      const isFavorite = newListings[listingIndex].isFavorited;
      newListings[listingIndex].isFavorited = !isFavorite;
      setListings(newListings);
    }
  };

  const goToSearch = async () => {
    try {
      const { lrpUrl } = await import(/* webpackChunkName: "lrpUrl" */ '@ecg-marktplaats/aurora-node-url-builder');
      const link = lrpUrl({
        categories: {
          l1Category: {
            key: 'auto-s',
          },
        },
        attributes: {
          undefined: appliedFilters.map((filterId) => ({
            attributeValueId: filterId,
          })),
        },
      });
      window.location.assign(link);
    } catch {}
  };

  return (
    <div ref={infiniteRef} className={`${Classes.root} ${isInApp ? Classes.embedded : ''}`}>
      <div className={Classes.filterRef} ref={filterRef} />
      <section className={Classes.filters}>
        <Pills className={Classes.pillsContainer}>
          {Boolean(l1Filters.length) && FILTERS[pageKey].includes('l1') && (
            <Pill
              className={Classes.reversePill}
              onClick={(e) => toggleFilter(e, 'l1', 0)}
              translations={{
                ariaLabelDefault: 'Speelgoed filter',
                ariaLabelActive: 'Speelgoed filter actief',
              }}
            >
              {Boolean(l1Filters.length) && <span className={Classes.filterCounter}>{l1Filters.length}</span>}
              Speelgoed
            </Pill>
          )}
          {l1Filters.length < 1 && FILTERS[pageKey].includes('l1') && (
            <IconPill
              icon="arrow-down-regular"
              className={Classes.reversePill}
              onClick={(e) => toggleFilter(e, 'l1', 0)}
              translations={{
                ariaLabelDefault: 'Speelgoed filter',
                ariaLabelActive: 'Speelgoed filter actief',
              }}
            >
              {Boolean(l1Filters.length) && <span className={Classes.filterCounter}>{l1Filters.length}</span>}
              Speelgoed
            </IconPill>
          )}
          {Boolean(searchFilters) &&
            searchFilters.map((filter) => (
              <IconPill
                icon="arrow-down-regular"
                key={filter.key}
                className={Classes.reversePill}
                onClick={(e) => toggleFilter(e, filter.key, filter.key, filter.attributeGroup)}
                translations={{
                  ariaLabelDefault: filter.label || '',
                  ariaLabelActive: filter.label || '',
                }}
              >
                {filter.label}
              </IconPill>
            ))}
          {Boolean(Object.keys(modelFilter).length) && (
            <IconPill
              icon="arrow-down-regular"
              className={`${Classes.reversePill} ${Classes.modelFilter}`}
              onClick={(e) => toggleFilter(e, 'model', 'model')}
              translations={{
                ariaLabelDefault: 'Model filter',
                ariaLabelActive: 'Model filter actief',
              }}
            >
              Model
            </IconPill>
          )}
          {FILTERS[pageKey].includes('price') && (
            <IconPill
              icon="arrow-down-regular"
              className={Classes.reversePill}
              onClick={(e) => toggleFilter(e, 'price', 'price')}
              translations={{
                ariaLabelDefault: 'Prijs filter',
                ariaLabelActive: 'Prijs filter actief',
              }}
            >
              Prijs
            </IconPill>
          )}
          {l1Filters.map((l1) => {
            const activeFilterCount = l2Filters.filter(({ groupId }) => groupId === l1.value).length;
            const PillComponent = activeFilterCount > 0 ? Pill : IconPill;

            return (
              <PillComponent
                key={l1.id}
                icon={activeFilterCount > 0 ? 'arrow-up-regular' : 'arrow-down-regular'}
                className={Classes.reversePill}
                onClick={(e) => toggleFilter(e, 'l1', Number(l1.value))}
                translations={{
                  ariaLabelDefault: l1.labelText || '',
                  ariaLabelActive: l1.labelText || '',
                }}
              >
                {Boolean(activeFilterCount) && <span className={Classes.filterCounter}>{activeFilterCount}</span>}
                {l1.labelText}
              </PillComponent>
            );
          })}
          {!isInApp && pageKey === 'car-upgrade' && (
            <div className={Classes.fullSearchLink}>
              <Link onClick={goToSearch}>
                <Icon name="arrow-right-regular" color="action" />
                Alle filters
              </Link>
            </div>
          )}
        </Pills>
        {searchLink && (
          <UtilsBlock as="div" p="s" className={Classes.searchLink}>
            <Link href={searchLink} iconRight="external-link">
              Bekijk al het aanbod
            </Link>
          </UtilsBlock>
        )}
      </section>

      <div className={Classes.listings}>
        <div className={Classes.listingsGrid}>
          {Boolean(listings) &&
            listings.map((item, i) => (
              <SearchFeedItem listing={item} key={`${item.title}-${i}`} toggleFavorite={toggleFavorite} />
            ))}
          {isLoading && (
            <div className={Classes.loadingIndicator}>
              <Spinner />
            </div>
          )}
        </div>
        {currentFilterId !== null && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <div className="hz-Overlay custom-Overlay" onClick={(e) => toggleFilter(e, null, null)} />
        )}
      </div>

      <div
        className={`hz-Modal hz-Modal--bottom-sheet ${Classes.filterPanel} ${currentFilterId === null ? Classes.hidden : Classes.open}`}
        style={filterPanelOffset}
      >
        <div className="hz-Modal-Header">
          <div className="hz-Modal-Header-Button" />
          <h5 className="hz-Title hz-Title--title5">
            {currentFilterId === 0
              ? 'Speelgoed'
              : searchCategories.l1Categories.find(({ value }) => value === currentFilterId)?.labelText}
            {pageKey === 'car-upgrade' && currentFilterId !== 0 ? t(`cars.search.fields.${currentFilterId}`) : ''}
            {currentFilterId === 'l2Category' ? ' (maximaal 3)' : ''}
          </h5>
          <div className="hz-Modal-Header-Button">
            <button
              className="hz-Button hz-Button--tertiary hz-Button--icon-button"
              aria-label="Close"
              onClick={(e) => toggleFilter(e, null, null)}
            >
              <Icon name="close" color="action" />
            </button>
          </div>
        </div>
        <div className="hz-Modal-Content" ref={filterValuesRef}>
          {currentFilterId === 'model' && (
            <>
              <Tabs className={Classes.modelTabs} activeTab={activeTab} setActiveTab={setActiveTab} isFluid>
                {Object.keys(modelFilter).map((modelForBrand, index) => (
                  <Tab key={index} label={modelForBrand} />
                ))}
              </Tabs>
              {Object.keys(modelFilter).map((modelForBrand, index) => (
                <TabPanel className={Classes.modelTabPanel} activeTab={activeTab} tab={index} key={index}>
                  <DumbSelectionGroup>
                    {modelFilter[modelForBrand].options.map((option) => (
                      <Checkbox
                        id={option.id}
                        key={option.value}
                        value={option.value}
                        onChange={() => onFilter(option, 'filter')}
                        isChecked={appliedFilters.includes(option.id)}
                        labelText={option.labelText}
                      />
                    ))}
                  </DumbSelectionGroup>
                </TabPanel>
              ))}
            </>
          )}
          {currentFilterId !== 'model' && (
            <DumbSelectionGroup>
              {currentFilterId === 'price' &&
                filterOptions.map((option) => (
                  <Radio
                    id={option.id}
                    key={option.value}
                    value={option.value}
                    onChange={() => onFilter(option, 'price')}
                    isChecked={priceFilter === option.value}
                    labelText={option.labelText}
                    icon={option.icon ? option.icon : null}
                  />
                ))}
              {currentFilterId === 'mileage' && (
                <>
                  <Dropdown
                    id="mileage"
                    labelText="Van"
                    options={
                      mileageFilter[1]
                        ? MILEAGE_OPTIONS.filter(({ value }) => value < Number(mileageFilter[1]))
                        : MILEAGE_OPTIONS
                    }
                    onChange={(e) => onFilter(e.target.value, 'mileageFrom')}
                    value={mileageFilter[0]}
                  />
                  <Dropdown
                    id="mileage"
                    labelText="Tot"
                    options={
                      mileageFilter[0]
                        ? MILEAGE_OPTIONS.filter(({ value }) => value > Number(mileageFilter[0]))
                        : MILEAGE_OPTIONS
                    }
                    onChange={(e) => onFilter(e.target.value, 'mileageTo')}
                    value={mileageFilter[1]}
                  />
                </>
              )}
              {currentFilterId !== 'price' &&
                currentFilterId !== 'mileage' &&
                typeof currentFilterId === 'number' &&
                filterOptions.map((option) => (
                  <Checkbox
                    id={option.id}
                    key={option.value}
                    value={option.value}
                    onChange={() => onFilter(option, 'category')}
                    isChecked={
                      currentFilterId === 0
                        ? l1Filters.find(({ value }) => value === option.value)
                        : l2Filters.find(({ value }) => value === option.value)
                    }
                    labelText={option.labelText}
                    icon={option.icon ? option.icon : null}
                  />
                ))}
              {currentFilterId !== 'price' &&
                typeof currentFilterId !== 'number' &&
                currentFilterId !== 'model' &&
                currentFilterId !== 'mileage' &&
                filterOptions
                  .filter(({ hidden }) => !hidden || showMoreFilterOptions)
                  .map((option) => (
                    <Checkbox
                      id={option.id}
                      key={option.value}
                      value={option.value}
                      disabled={
                        !appliedFilters.includes(option.id) &&
                        currentFilterId === 'l2Category' &&
                        appliedFilters.filter((filter) => typeof filter === 'string' && filter.includes('cat-'))
                          .length >= MAX_L2_FILTERS
                      }
                      onChange={() => onFilter(option, currentFilterId === 'l2Category' ? 'l2Category' : 'filter')}
                      isChecked={appliedFilters.includes(option.id)}
                      labelText={option.labelText}
                    />
                  ))}
              {currentFilterId === 'options' && (
                <Link onClick={() => setShowMoreFilterOptions(!showMoreFilterOptions)} className={Classes.showMoreLink}>
                  {showMoreFilterOptions ? 'Toon minder...' : 'Toon meer...'}
                </Link>
              )}
            </DumbSelectionGroup>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchFeed;
